import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import branding from './branding.json';
import 'reactflow/dist/style.css';
import './components/graphs/GraphStyles.css';
import { Layout } from './components/layout/Layout';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginModal } from './components/auth/LoginModal';
import { ConsentModal } from './components/auth/ConsentModal';
import { Landing } from './pages/Landing';
import InviteRegistration from './pages/InviteRegistration';
import Sessions from './pages/Sessions';
import { NewSession } from './pages/NewSession';
import { SessionDetail } from './pages/SessionDetail';
import { Personas } from './pages/Personas';
import { PersonaDetail } from './pages/PersonaDetail';
import { PersonaForm } from './pages/PersonaForm';
import { PersonaChat } from './pages/PersonaChat';
import { ChatSessions } from './pages/ChatSessions';
import { Artifacts } from './pages/Artifacts';
import { ArtifactDetail } from './pages/ArtifactDetail';
import { Graph } from './pages/Graph';
import { CollaborativeGraph } from './components/graphs/CollaborativeGraph';
import Workflows from './pages/Workflows';
import { PhaseDependencyGraph } from './components/sessions/PhaseDependencyGraph';
import { Teams } from './pages/Teams';
import { NewTeam } from './pages/NewTeam';
import { TeamDetail } from './pages/TeamDetail';
import { TeamEdit } from './pages/TeamEdit';
import { TeamComparison } from './pages/TeamComparison';
import { ArtifactoryPage } from './pages/Artifactory';
import strategyRegistry from './services/strategyRegistry';
import ModelDashboard from './pages/ModelDashboard';
import ModelSettings from './pages/ModelSettings';
import { ModelComparison } from './components/models/ModelComparison';
import { useTheme } from './contexts/ThemeContext';
import ToolExplorer from './pages/ToolExplorer';
import WorkflowDetails from './pages/WorkflowDetails';
import WorkflowExecution from './pages/WorkflowExecution';
import TimelinePage from './pages/TimelinePage';
import NotificationsPage from './pages/NotificationsPage';
import { Documentation } from './pages/Documentation';
import { useQuery } from '@tanstack/react-query';
import { listDocs } from './services/docsService';
import WebSocketTestPage from './pages/WebSocketTestPage';
import PersonaForge from './pages/PersonaForge';
import { ManagePersonas } from './pages/ManagePersonas';
import { Profile } from './pages/Profile';
import { Legal } from './pages/Legal';
import { Settings } from './pages/Settings';
import AdminDashboard from './pages/AdminDashboard';
import AdminChat from './pages/AdminChat';
import UserDashboard from './components/dashboards/UserDashboard';
import { AdminLayout } from './components/layout/AdminLayout';
import AdminContent from './pages/AdminContent';
import AdminLogs from './pages/AdminLogs';
import { WhatsNewModalController } from './components/changelog/WhatsNewModalController';
import SkillsHub from './pages/skills/SkillsHub';
import Gymnasium from './pages/Gymnasium';
// A2: Work Management
import Goals from './pages/Goals';
import Projects from './pages/Projects';
import Tasks from './pages/Tasks';
import GoalDetail from './pages/GoalDetail';
import WorkChat from './pages/WorkChat';
import PulseHistory from './pages/PulseHistory';
import ProjectDetail from './pages/ProjectDetail';
import TaskDetail from './pages/TaskDetail';
const Calendar = React.lazy(() => import('./pages/Calendar'));
const DraftBoard = React.lazy(() => import('./pages/DraftBoard'));
import { SkillDetail } from './pages/skills/SkillDetail';
import { SkillSources } from './pages/skills/SkillSources';
import { SkillReviewQueue } from './pages/skills/SkillReviewQueue';
import DashboardIndex from './components/dashboards/DashboardIndex';
import PersonaDashboard from './components/dashboards/PersonaDashboard';
import PersonaDashboardDetail from './components/dashboards/PersonaDetail';
import WorkflowDashboard from './components/dashboards/WorkflowDashboard';
import WorkflowDashboardDetail from './components/dashboards/WorkflowDetail';
import ArtifactoryDashboard from './components/dashboards/ArtifactoryDashboard';
import ArtifactoryDashboardDetail from './components/dashboards/ArtifactoryDetail';
import DashboardLayout from './components/dashboards/DashboardLayout';

// Create a client
const queryClient = new QueryClient();

// Create a wrapper for ModelComparison that gets darkMode from context
const ModelComparisonWrapper = () => {
  const { darkMode } = useTheme();
  return <ModelComparison darkMode={darkMode} />;
};

// Create a static login component that won't be remounted
const StaticLogin = React.memo(() => {
  console.log("[APP] Rendering static login component");
  return <Login />;
});

// Create an initialization component that ensures proper loading sequence
const AppInitializer = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [strategyInitialized, setStrategyInitialized] = useState(false);
  
  // Initialize strategy registry only after auth is determined
  useEffect(() => {
    // Only initialize if auth check is complete (either logged in or not)
    if (!loading) {
      const initializeStrategies = async () => {
        try {
          const { default: strategyRegistry } = await import('./services/strategyRegistry');
          await strategyRegistry.initialize();
          setStrategyInitialized(true);
        } catch (error) {
          console.error("Failed to initialize strategy registry:", error);
          // Still mark as initialized to prevent blocking the app
          setStrategyInitialized(true);
        }
      };
      
      initializeStrategies();
    }
  }, [loading]);
  
  // Show loading indicator while initializing
  if (loading || !strategyInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return children;
};

// Create a layout component that wraps the outlet
const ProtectedLayout = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <Layout>
      <ConsentModal />
      {isAuthenticated && <WhatsNewModalController />}
      {isAuthenticated ? (
        <Outlet />
      ) : (
        <Landing />
      )}
    </Layout>
  );
};

// Create the router configuration
const MyDashboardRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  return <Navigate to={`/dashboards/user/${user.id}`} replace />;
};

// Sanitize :version URL param against the slug regex before redirecting,
// so URL-encoded payloads ("%2F..%2Fadmin%3Fkey=") can't bleed through.
const DOCS_SLUG_REGEX = /^[a-z0-9.-]+$/;
const ChangelogVersionRedirect: React.FC = () => {
  const { version } = useParams<{ version: string }>();
  if (!version || !DOCS_SLUG_REGEX.test(version)) {
    return <Navigate to="/docs" replace />;
  }
  return <Navigate to={`/docs/${version}`} replace />;
};

// /changelog (no version) → latest release notes with Release Notes group
// auto-expanded. Uses the same ['docs-list'] query key Documentation /
// useWhatsNew use — single round-trip in the common case.
const ChangelogLatestRedirect: React.FC = () => {
  const { data, isLoading } = useQuery(['docs-list'], listDocs, { staleTime: 300000 });
  if (isLoading) return null;
  const latest = data?.docs.find((d) => d.group === 'Release Notes');
  if (!latest) return <Navigate to="/docs" replace />;
  return <Navigate to={`/docs/${latest.slug}?group=Release%20Notes`} replace />;
};

const router = createBrowserRouter([
  {
    path: "/login",
    element: <StaticLogin />,
  },
  {
    path: "/legal",
    element: <Legal />,
  },
  {
    path: "/invite/:token",
    element: <InviteRegistration />,
  },
  {
    path: "/",
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: <Home />
      },
      {
        path: "sessions",
        children: [
          {
            index: true,
            element: <Sessions />
          },
          {
            path: "new",
            element: <NewSession />
          },
          {
            path: ":sessionId",
            element: <SessionDetail />
          },
          {
            path: ":sessionId/artifactory",
            element: <ArtifactoryPage />
          }
        ]
      },
      {
        path: "personas",
        children: [
          {
            index: true,
            element: <Personas />
          },
          {
            path: "new",
            element: <PersonaForm />
          },
          {
            path: ":personaId",
            element: <PersonaDetail />
          },
          {
            path: ":personaId/edit",
            element: <PersonaForm />
          },
          {
            path: "forge",
            element: <PersonaForge />
          },
          {
            path: "draft-board",
            element: <React.Suspense fallback={<div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full" /></div>}><DraftBoard /></React.Suspense>,
          },
          {
            path: "iomgr",
            element: <ManagePersonas />
          }
        ]
      },
      {
        path: "chat/persona/:personaId",
        element: <PersonaChat />
      },
      {
        path: "chat/persona/:personaId/:sessionId",
        element: <PersonaChat />
      },
      {
        path: "chat/sessions",
        element: <Sessions />
      },
      {
        path: "artifacts",
        children: [
          {
            index: true,
            element: <Artifacts />
          },
          {
            path: ":artifactId",
            element: <ArtifactDetail />
          }
        ]
      },
      {
        path: "artifactory",
        children: [
          {
            index: true,
            element: <ArtifactoryPage />
          },
          {
            path: ":sessionId",
            element: <ArtifactoryPage />
          }
        ]
      },
      {
        path: "graph",
        children: [
          {
            path: "session/:sessionId",
            element: <Graph />
          },
          {
            path: "lineage/:lineageId",
            element: <Graph />
          },
          {
            path: "collaborative/:sessionId",
            element: <CollaborativeGraph />
          }
        ]
      },
      {
        // Documentation BETA — in-app docs surface backed by /api/docs/*.
        path: "docs",
        element: <Documentation />,
      },
      {
        path: "docs/:slug",
        element: <Documentation />,
      },
      {
        // Bookmark survival — old /changelog routes redirect to the new
        // docs system. /changelog/:version is sanitized against SLUG_REGEX
        // so URL-decoded payloads (../admin?key=) can't bleed through.
        path: "changelog",
        element: <ChangelogLatestRedirect />,
      },
      {
        path: "changelog/:version",
        element: <ChangelogVersionRedirect />,
      },
      {
        path: "workflows",
        children: [
          {
            index: true,
            element: <Workflows />
          },
          {
            path: "new",
            element: <Workflows />
          },
          {
            path: "timeline",
            element: <TimelinePage />
          },
          {
            path: ":workflowId",
            element: <WorkflowDetails />
          },
          {
            path: ":workflowId/edit",
            element: <Workflows />
          },
          {
            path: ":workflowId/dependencies",
            element: <PhaseDependencyGraph />
          },
          {
            path: "executions/:executionId",
            element: <WorkflowExecution />
          }
        ]
      },
      {
        path: "skills",
        children: [
          { index: true, element: <SkillsHub /> },
          {
            path: "sources",
            element: <ProtectedRoute adminOnly={true}><SkillSources /></ProtectedRoute>
          },
          {
            path: "pending",
            element: <ProtectedRoute adminOnly={true}><SkillReviewQueue /></ProtectedRoute>
          },
          { path: ":skillName", element: <SkillDetail /> }
        ]
      },
      {
        path: "gymnasium",
        children: [
          { index: true, element: <Gymnasium /> },
          { path: "arena", element: <Gymnasium /> },
          { path: "gauntlets", element: <Gymnasium /> },
          { path: "gauntlets/:gauntletId", element: <Gymnasium /> },
          { path: "runs/:runId", element: <Gymnasium /> },
          { path: "radar", element: <Gymnasium /> },
          { path: "rankings", element: <Gymnasium /> },
        ]
      },
      // A2+A3: Work Management routes
      { path: "work/chat", element: <WorkChat /> },
      {
        path: "goals",
        children: [
          { index: true, element: <Goals /> },
          { path: ":goalId", element: <GoalDetail /> },
        ],
      },
      {
        path: "projects",
        children: [
          { index: true, element: <Projects /> },
          { path: ":projectId", element: <ProjectDetail /> },
        ],
      },
      {
        path: "tasks",
        children: [
          { index: true, element: <Tasks /> },
          { path: ":taskId", element: <TaskDetail /> },
        ],
      },
      { path: "calendar", element: <React.Suspense fallback={<div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" /></div>}><Calendar /></React.Suspense> },
      { path: "heartbeat", element: <PulseHistory /> },
      {
        path: "teams",
        children: [
          {
            index: true,
            element: <Teams />
          },
          {
            path: "new",
            element: <NewTeam />
          },
          {
            path: ":teamId",
            element: <TeamDetail />
          },
          {
            path: ":teamId/edit",
            element: <TeamEdit />
          },
          {
            path: "compare",
            element: <TeamComparison />
          }
        ]
      },
      {
        path: "models",
        children: [
          {
            index: true,
            element: <ModelDashboard />
          },
          {
            path: "settings",
            element: <ModelSettings />
          },
          {
            path: "comparison",
            element: <ModelComparisonWrapper />
          }
        ]
      },
      {
        path: "tools",
        element: <ToolExplorer />
      },
      {
        path: "notifications",
        element: <NotificationsPage />
      },
      {
        path: "profile",
        element: <Profile />
      },
      {
        path: "settings",
        element: <Settings />
      },
      {
        path: "dashboards",
        element: <DashboardLayout />,
        children: [
          { index: true, element: <DashboardIndex /> },
          { path: "personas", element: <PersonaDashboard /> },
          { path: "personas/:personaId", element: <PersonaDashboardDetail /> },
          { path: "workflows", element: <WorkflowDashboard /> },
          { path: "workflows/:workflowId", element: <WorkflowDashboardDetail /> },
          { path: "artifactory", element: <ArtifactoryDashboard /> },
          { path: "artifactory/:manifestId", element: <ArtifactoryDashboardDetail /> },
          { path: "me", element: <MyDashboardRedirect /> },
          { path: "user/:userId", element: <UserDashboard /> },
        ],
      },
      {
        path: "admin",
        element: <ProtectedRoute adminOnly={true}><AdminLayout /></ProtectedRoute>,
        children: [
          {
            index: true,
            element: <AdminDashboard />
          },
          {
            path: "content",
            element: <AdminContent />
          },
          {
            path: "logs",
            element: <AdminDashboard activeTab="logs" />
          },
          {
            path: "users",
            element: <AdminDashboard activeTab="users" />
          },
          {
            path: "invites",
            element: <AdminDashboard activeTab="invites" />
          },
          {
            path: "templates",
            element: <AdminDashboard activeTab="templates" />
          },
          {
            path: "chat",
            element: <AdminChat />
          }
        ]
      },
      {
        path: "websocket-test",
        element: <WebSocketTestPage />
      }
    ]
  }
]);

function App() {
  // Set document title based on branding
  useEffect(() => {
    document.title = branding.appName;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <AppInitializer>
              <LoginModal />
              <RouterProvider router={router} />
            </AppInitializer>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
// This comment is intentionally left blank to remove these lines
